import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Star, Info } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface PricingPlan {
  id: number | string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  isPopular: boolean;
}

const fallbackPlans: PricingPlan[] = [
  {
    id: 1,
    name: 'Starter',
    price: '$79',
    period: '/month',
    description: 'Get online with a professional site that captures leads — ideal for businesses starting from scratch.',
    features: [
      '5-page professional website',
      'Contact form with email notifications',
      'Google Business Profile optimization',
      'On-page SEO (titles, schema, sitemap)',
      'SSL certificate included',
      'Daily backups & uptime monitoring',
      'Software updates included',
      'Basic maintenance & email support',
    ],
    isPopular: false,
  },
  {
    id: 2,
    name: 'Growth',
    price: '$199',
    period: '/month',
    description: 'Turn visitors and past customers into repeat business with booking, texting, and loyalty tools.',
    features: [
      'Everything in Starter',
      'Online booking system',
      '2-way SMS appointment notifications',
      '300 text messages/month',
      'Digital loyalty program',
      'GBP review request automation',
      'Ecommerce (up to 25 products)',
      'Full maintenance & priority support',
    ],
    isPopular: true,
  },
  {
    id: 3,
    name: 'Accelerate',
    price: '$499',
    period: '/month',
    description: 'Active paid ad management on top of everything — for businesses ready to grow their customer base.',
    features: [
      'Everything in Growth',
      'Google Ads or Meta Ads management',
      'Ad spend billed separately',
      '2,000 text messages/month',
      'Conversion tracking & ad reporting',
      'A/B landing page testing',
      'Same-day priority support',
    ],
    isPopular: false,
  },
  {
    id: 4,
    name: 'Website Redesign',
    price: '$999',
    period: 'one-time',
    description: 'One-time fee to build or fully redesign your site. Waived entirely after 12 months on any plan.',
    features: [
      'Custom responsive design',
      'Mobile-first development',
      'Modern UI/UX best practices',
      'Full SEO foundation built-in',
      'Contact form & CTAs configured',
      'Analytics & conversion tracking',
      'Delivered in 2–3 weeks',
      'First month free for new clients',
    ],
    isPopular: false,
  },
];

const fallbackFaqs = [
  {
    id: 1,
    question: 'Is $79/month really enough to get a professional site?',
    answer: 'Yes. Starter includes a hosted 4–5 page website with SSL, SEO, contact form, and Google Business Profile optimization. It beats a free Wix site on every dimension — speed, SEO, security, and support.',
  },
  {
    id: 2,
    question: 'What does "first month free" mean?',
    answer: 'We build your site and waive the first month\'s fee. You see it live before committing. The $999 design fee is a separate one-time charge — pay it upfront, roll it into your monthly rate, or stay 12 months and it\'s waived entirely.',
  },
  {
    id: 3,
    question: 'When should I upgrade from Starter to Growth?',
    answer: 'Most clients upgrade after 3–6 months once they trust the service and want to add booking, SMS notifications, and a loyalty program. There\'s no pressure — we\'ll help you decide when it makes sense for your business.',
  },
  {
    id: 4,
    question: 'Can I cancel anytime?',
    answer: 'Yes — all plans are month-to-month with no long-term contracts. If you cancel before 12 months and haven\'t paid the design fee, the $999 balance becomes due. Stay 12 months and it\'s waived entirely.',
  },
];

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>(fallbackPlans);
  const [faqs, setFaqs] = useState(fallbackFaqs);

  useEffect(() => {
    // Fetch pricing
    api.get('/pricing')
      .then(res => {
        const packages = res.data?.data?.packages || res.data?.packages || res.data;
        if (Array.isArray(packages) && packages.length > 0) {
          const mapped: PricingPlan[] = packages.map((p: any) => ({
            id: p.id,
            name: p.name,
            price: p.duration === 'per month' || p.duration === '/month'
              ? `$${Math.round(parseFloat(p.price))}`
              : `$${Number(p.price).toLocaleString()}`,
            period: p.duration === 'one-time' ? 'one-time' : p.duration === 'per month' ? '/month' : p.duration,
            description: p.description,
            features: Array.isArray(p.features) ? p.features : (typeof p.features === 'string' ? p.features.split('\n').filter(Boolean) : []),
            isPopular: p.isPopular || false,
          }));
          setPricingPlans(mapped);
        }
      })
      .catch(() => {});

    // Fetch pricing FAQs
    api.get('/faq')
      .then(res => {
        const faqData = res.data?.data || res.data?.faqs || res.data;
        if (Array.isArray(faqData) && faqData.length > 0) {
          const pricingFaqs = faqData
            .filter((f: any) => f.category === 'Pricing' || f.category === 'Billing')
            .slice(0, 4)
            .map((f: any, i: number) => ({
              id: f.id || i + 1,
              question: f.question,
              answer: f.answer,
            }));
          if (pricingFaqs.length > 0) setFaqs(pricingFaqs);
        }
      })
      .catch(() => {});
  }, []);

  const handleGetStarted = (plan: PricingPlan) => {
    navigate('/contact', { 
      state: { 
        selectedPackage: plan.name,
        packagePrice: plan.price,
        packageId: plan.id
      } 
    });
  };

  return (
    <>
      <Helmet>
        <title>Pricing - iModernize | Web Design & SEO Services</title>
        <meta name="description" content="Simple monthly plans for Manchester NH businesses. Get a professional website, booking, SMS marketing, and ad management starting at $79/month. First month free for new clients." />
        <meta name="keywords" content="web design pricing, SEO services cost, website redesign pricing, web hosting plans, custom app development cost, affordable web design New Hampshire" />
      </Helmet>

      <div className="min-h-screen bg-white py-20">
        <div className="container-custom">
          {/* Hero Section */}
          <div className="text-center mb-20">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Start at <span className="font-semibold text-gray-900">$79/month</span> with a professional site, SEO, and support.
              Upgrade when you're ready. New clients get their <span className="font-semibold text-blue-600">first month free</span>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            {pricingPlans.filter(p => p.period === '/month').map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl border-2 transition-all duration-300 ${
                  plan.isPopular 
                    ? 'border-blue-600 shadow-xl shadow-blue-600/10 transform md:scale-105' 
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-lg'
                }`}
              >
                {plan.isPopular && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold flex items-center shadow-lg">
                      <Star className="h-4 w-4 mr-2 fill-current" />
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="p-8">
                  <div className="text-center mb-8 pb-8 border-b border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {plan.name}
                    </h3>
                    <p className="text-gray-600 mb-6 min-h-[3rem] text-sm">
                      {plan.description}
                    </p>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-gray-900">
                        {plan.price}
                      </span>
                      <span className="text-gray-500 text-base ml-1">/month</span>
                    </div>
                    <p className="text-xs text-green-700 font-semibold">First month free</p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <div className="flex-shrink-0 mt-0.5">
                          <Check className="h-5 w-5 text-blue-600" strokeWidth={2.5} />
                        </div>
                        <span className="ml-3 text-gray-700 text-sm leading-relaxed">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleGetStarted(plan)}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      plan.isPopular 
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg' 
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200 border border-gray-300'
                    }`}
                  >
                    Get Started Free
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Transparent Website Build Fee Section */}
          <div className="max-w-5xl mx-auto mb-20">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 md:p-10">
              <div className="flex items-start gap-4 mb-8">
                <div className="p-3 bg-amber-100 rounded-xl flex-shrink-0">
                  <Info className="h-6 w-6 text-amber-700" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Website Build Fee — $999{' '}
                    <span className="text-base font-normal text-gray-500">(waived after 12 months on any plan)</span>
                  </h3>
                  <p className="text-gray-600">
                    Your first month is free — we build the site, you see it live, and then decide. After that,
                    you can pay the $999 build fee once or roll it into your monthly rate with nothing due upfront.
                    Stay 12 months and the fee disappears entirely.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Option A */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <h4 className="font-bold text-gray-900 mb-1">Option A — Pay $999 After Month 1</h4>
                  <p className="text-sm text-gray-500 mb-5">Site is yours immediately. Standard monthly rate going forward.</p>
                  <div className="space-y-3">
                    {[
                      { label: 'Starter', monthly: '$79/mo', year1: '$1,868' },
                      { label: 'Growth', monthly: '$199/mo', year1: '$3,188' },
                      { label: 'Accelerate', monthly: '$499/mo', year1: '$6,488' },
                    ].map(row => (
                      <div key={row.label} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <span className="text-sm text-gray-700">{row.label}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-gray-900">{row.monthly}</span>
                          <span className="text-xs text-gray-400 block">~{row.year1} year&nbsp;1</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Option B */}
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-bold text-gray-900">Option B — Roll into Monthly</h4>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">$0 upfront</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-5">Higher rate for 12 months, then drops. Site ownership at 12 months. Saves ~$229 in year&nbsp;1.</p>
                  <div className="space-y-3">
                    {[
                      { label: 'Starter', monthly: '$149/mo → $79/mo', year1: '$1,639' },
                      { label: 'Growth', monthly: '$269/mo → $199/mo', year1: '$2,959' },
                      { label: 'Accelerate', monthly: '$569/mo → $499/mo', year1: '$6,259' },
                    ].map(row => (
                      <div key={row.label} className="flex justify-between items-center py-2 border-b border-blue-100 last:border-0">
                        <span className="text-sm text-gray-700">{row.label}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-gray-900">{row.monthly}</span>
                          <span className="text-xs text-gray-400 block">~{row.year1} year&nbsp;1</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-6 flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                Year 1 estimates assume 11 paid months (after the free first month). No credit card needed to get started.
              </p>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-24 bg-gray-50 rounded-3xl p-12">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq) => (
                <div key={faq.id} className="bg-white rounded-xl p-8 border border-gray-200 hover:border-blue-300 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-24 text-center">
            <div className="bg-blue-600 rounded-3xl p-12 text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Get Your Business Online?
              </h2>
              <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
                First month is on us. We build your site, you see it live, then decide.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PricingPage;